/**
 * Semantic categories for server-recorded service activity.
 * Used by the Repair Order timeline and the Service Dashboard so the same
 * event keeps the same colour family in both places. This does not change
 * audit storage.
 */

export const SERVICE_ACTIVITY_CATEGORIES = [
  "work_timer",
  "assignment",
  "status",
  "customer_report",
  "customer_approval",
  "finance",
  "parts",
  "general",
] as const;

export type ServiceActivityCategory = (typeof SERVICE_ACTIVITY_CATEGORIES)[number];

export const SERVICE_ACTIVITY_CATEGORY_LABEL: Record<ServiceActivityCategory, string> = {
  work_timer: "Work timer",
  assignment: "Assignment",
  status: "Status",
  customer_report: "Customer",
  customer_approval: "Approval",
  finance: "Finance",
  parts: "Parts",
  general: "System",
};

function includesAny(value: string, needles: string[]): boolean {
  return needles.some((needle) => value.includes(needle));
}

/** Map a stored audit action onto one presentation category. Unknown actions stay general. */
export function categorizeServiceActivity(action: string | null | undefined): ServiceActivityCategory {
  const key = String(action ?? "").trim().toLowerCase();
  if (!key) return "general";

  if (key === "repair_started_without_approval" || key.startsWith("work_session_") || key.includes("work timer")) return "work_timer";

  if (
    key === "technician_assigned"
    || includesAny(key, ["assigned technician", "technician assigned", "pic reassigned", "pic assigned", "reassigned"])
  ) {
    return "assignment";
  }

  if (includesAny(key, ["customer approved", "customer declined", "approval recorded"])) {
    return "customer_approval";
  }

  if (
    key.includes("parts")
    && !includesAny(key, ["invoice", "finance entered", "selling price"])
  ) {
    return "parts";
  }

  if (
    key === "service_report_entry_added"
    || includesAny(key, [
      "service report",
      "proposal",
      "customer-facing",
      "notification",
      "sent to customer",
      "report sent",
    ])
  ) {
    return "customer_report";
  }

  if (
    includesAny(key, [
      "invoice",
      "payment",
      "paid",
      "selling price",
      "finance",
      "charge waived",
      "non-billable",
      "classified as non-billable",
    ])
  ) {
    return "finance";
  }

  if (
    key === "status_changed"
    || key === "repair_completed"
    || key.startsWith("status changed")
    || includesAny(key, ["repair completed", "item collected", "moved to"])
  ) {
    return "status";
  }

  return "general";
}
